import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const teamDropdownItems = [
    { label: 'Attorneys', path: '/our-team#attorneys' },
    { label: 'Paralegals & Staff', path: '/our-team#staff' },
  ];

  const practiceAreasDropdownItems = [
    { label: 'Auto Accidents', path: '/practice-areas/auto-accidents' },
    { label: 'Catastrophic Injury Cases', path: '/practice-areas/catastrophic-injury' },
    { label: 'Premises Liability Cases', path: '/practice-areas/premises-liability' },
    { label: 'Trucking Accidents', path: '/practice-areas/trucking-accidents' },
    { label: 'Workers\' Compensation', path: '/practice-areas/workers-compensation' },
    { label: 'Wrongful Death', path: '/practice-areas/wrongful-death' },
  ];

  return (
    <>
      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${
          isScrolled ? 'shadow-md' : 'shadow-sm'
        }`}
        style={{ borderBottom: '1px solid rgba(226, 232, 240, 0.8)' }}
        data-testid="main-header"
      >
        <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 lg:h-[88px]">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" data-testid="logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/j5bpvf84_logo.png"
                alt="Rizzi Law Group"
                className="h-10 lg:h-14 w-auto"
                style={{ imageRendering: '-webkit-optimize-contrast' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav 
              className="hidden lg:flex items-center gap-7" 
              data-testid="desktop-nav"
            >
              <Link to="/" className="font-medium text-slate-700 hover:text-primary transition-colors text-[16px]" data-testid="nav-home">
                Home
              </Link>

              {/* Our Team Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setActiveDropdown('team')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  className="flex items-center gap-1 font-medium text-slate-700 hover:text-primary transition-colors py-2 text-[16px]"
                  data-testid="nav-our-team"
                >
                  Our Team
                  <ChevronDown className="h-4 w-4" />
                </button>
                {activeDropdown === 'team' && (
                  <div className="absolute top-full left-0 w-56 bg-white border border-slate-200 shadow-xl rounded-md overflow-hidden">
                    {teamDropdownItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block px-5 py-3.5 text-slate-700 hover:bg-slate-50 hover:text-primary transition-colors text-[15px]"
                        data-testid={`nav-team-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Practice Areas Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setActiveDropdown('practice')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  className="flex items-center gap-1 font-medium text-slate-700 hover:text-primary transition-colors py-2 text-[16px]"
                  data-testid="nav-practice-areas"
                >
                  Practice Areas
                  <ChevronDown className="h-4 w-4" />
                </button>
                {activeDropdown === 'practice' && (
                  <div className="absolute top-full left-0 w-72 bg-white border border-slate-200 shadow-xl rounded-md overflow-hidden">
                    {practiceAreasDropdownItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block px-5 py-3.5 text-slate-700 hover:bg-slate-50 hover:text-primary transition-colors text-[15px]"
                        data-testid={`nav-practice-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/case-results" className="font-medium text-slate-700 hover:text-primary transition-colors text-[16px]" data-testid="nav-case-results">
                Case Results
              </Link>
              <Link to="/testimonials" className="font-medium text-slate-700 hover:text-primary transition-colors text-[16px]" data-testid="nav-testimonials">
                Testimonials
              </Link>
              <Link to="/blog" className="font-medium text-slate-700 hover:text-primary transition-colors text-[16px]" data-testid="nav-blog">
                Blog
              </Link>
              <Link to="/contact" className="font-medium text-slate-700 hover:text-primary transition-colors text-[16px]" data-testid="nav-contact">
                Contact
              </Link>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:block flex-shrink-0">
              <Link
                to="/contact"
                className="bg-primary text-white font-semibold hover:bg-primary/90 transition-all duration-300 flex items-center justify-center shadow-sm hover:shadow-md h-12 px-7 rounded-xl text-[15px]"
                data-testid="header-consultation-cta"
              >
                Free Consultation
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 -mr-2 text-slate-700"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 shadow-lg z-50" data-testid="mobile-menu">
            <div className="flex flex-col px-4 py-4">
              <Link to="/" className="font-medium text-slate-700 py-3 text-[16px] border-b border-slate-100" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-nav-home">
                Home
              </Link>
              <Link to="/our-team" className="font-medium text-slate-700 py-3 text-[16px] border-b border-slate-100" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-nav-our-team">
                Our Team
              </Link>
              <Link to="/practice-areas" className="font-medium text-slate-700 py-3 text-[16px] border-b border-slate-100" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-nav-practice-areas">
                Practice Areas
              </Link>
              <Link to="/case-results" className="font-medium text-slate-700 py-3 text-[16px] border-b border-slate-100" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-nav-case-results">
                Case Results
              </Link>
              <Link to="/testimonials" className="font-medium text-slate-700 py-3 text-[16px] border-b border-slate-100" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-nav-testimonials">
                Testimonials
              </Link>
              <Link to="/blog" className="font-medium text-slate-700 py-3 text-[16px] border-b border-slate-100" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-nav-blog">
                Blog
              </Link>
              <Link to="/contact" className="font-medium text-slate-700 py-3 text-[16px] border-b border-slate-100" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-nav-contact">
                Contact
              </Link>
              
              <div className="pt-4">
                <Link
                  to="/contact"
                  className="bg-primary text-white py-3 font-semibold text-center text-[15px] block rounded-xl"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-consultation-cta"
                >
                  Free Consultation
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
